import React, {useState, useRef} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {Button} from 'bonprix-design-system';
import {
    firstNameValidation,
    lastNameValidation,
    FirstNameValidationTexts,
    LastNameValidationTexts,
    OrderNumberValidationTexts,
    orderNumberValidation,
} from '@account/pages/withdrawal/inputFieldValidations';
import {emailValidation, EmailValidationTexts} from '@core/scripts/helper/form-input-validation/inputFieldValidations';
import ControlledInputField from '@account/shared-components/ControlledInputField';
import FriendlyCaptcha from '@core/scripts/components/FriendlyCaptcha.jsx';
import {FriendlyCaptchaProps} from '@core/scripts/types/FriendlyCaptcha';
import {doSendMetric} from '@core/scripts/helper/send-metric-helper';
import Url from '@core/scripts/helper/url.js';
import {fetchData} from '@account/helper/fetchDataHelper';
import {useTextsContext} from '@account/shared-components/providers/TextsProvider';
import WithdrawalTracking from '@account/pages/withdrawal/WithdrawalTracking';
import {HttpMethods} from "@account/shared-components/constants";

interface WithdrawalFormProps {
    isCaptchaEnabled: boolean;
    isCaptchaV2Enabled?: boolean;
    friendlyCaptchaSiteKey: string;
    withdrawOrderUrl: string;
    isApp: boolean;
    canNavigateBack: boolean;
    homeUrl: string;
    onError: (orderNumber: string, errorResponse: string) => void;
    onSuccess: (formData: FormValues, successResponse: string) => void;
}

export type FormValues = {
    email: string;
    firstName: string;
    lastName: string;
    orderNumber: string;
    captchaToken: string;
};

const BACKEND_ERROR_MESSAGE_TYPE = 'Error';

type FetchError = Error & {status?: number; body?: {messageType?: string; messageId?: string}};

const WithdrawalForm = ({
    isCaptchaEnabled,
    isCaptchaV2Enabled,
    friendlyCaptchaSiteKey,
    withdrawOrderUrl,
    isApp,
    canNavigateBack,
    homeUrl,
    onError,
    onSuccess,
}: WithdrawalFormProps) => {
    const {texts} = useTextsContext();

    const [isLoading, setIsLoading] = useState(false);
    const captchaTokenRef = useRef('');
    const captchaStartRef = useRef(0);
    const [captchaResetSwitch, setCaptchaResetSwitch] = useState(false);
    const [captchaCompletedSuccessfully, setCaptchaCompletedSuccessfully] = useState(false);

    const formMethods = useForm<FormValues>({
        mode: 'onTouched',
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            orderNumber: '',
            captchaToken: '',
        },
    });

    const {isValid} = formMethods.formState;

    const firstNameValidationMessages: FirstNameValidationTexts = {
        'firstname.invalid': texts['firstname.invalid'],
        first_name_max_length_exceeded: texts['first_name_max_length_exceeded'],
    };

    const lastNameValidationMessages: LastNameValidationTexts = {
        'lastname.invalid': texts['lastname.invalid'],
        last_name_max_length_exceeded: texts['last_name_max_length_exceeded'],
    };

    const emailValidationMessages: EmailValidationTexts = {
        'email.invalid': texts['email.invalid'],
        email_is_required: texts['email_is_required'],
        email_max_length_exceeded: texts['email_max_length_exceeded'],
    };

    const orderNumberValidationTexts: OrderNumberValidationTexts = {
        order_number_is_required: texts['order_number_is_required'],
        order_number_invalid: texts['order_number_invalid'],
    };

    const handleCaptchaStart = () => {
        captchaStartRef.current = Date.now();
    };

    const handleCaptchaErrorCallback = () => {
        captchaTokenRef.current = 'puzzle-error';
        setCaptchaCompletedSuccessfully(false);
        doSendMetric('captcha.withdrawal.error', 1.0);
    };

    const handleCaptchaVerifyCallback = (token: string) => {
        formMethods.setValue('captchaToken', token);
        if (token) {
            doSendMetric('captcha.withdrawal.duration', Date.now() - captchaStartRef.current);
        }
        setCaptchaCompletedSuccessfully(Boolean(token));
    };

    const friendlyCaptchaConfig: FriendlyCaptchaProps = {
        siteKey: friendlyCaptchaSiteKey,
        v2: isCaptchaV2Enabled,
        startMode: 'auto',
        onCaptchaStart: handleCaptchaStart,
        errorCallback: handleCaptchaErrorCallback,
        verifyCallback: handleCaptchaVerifyCallback,
        lang: 'de',
        resetSwitch: true,
    };

    // Also forget the token that was just rejected, otherwise the submit button stays enabled while the
    // widget re-solves and a second click would re-post the same token.
    const resetCaptcha = () => {
        setCaptchaResetSwitch(!captchaResetSwitch);
        setCaptchaCompletedSuccessfully(false);
        formMethods.setValue('captchaToken', '');
    };

    const isSubmitButtonEnabled = () => {
        return isValid && (!isCaptchaEnabled || captchaCompletedSuccessfully);
    };

    const handleSubmit = (formData: FormValues) => {
        setIsLoading(true);

        const requestBody = {
            orderId: formData.orderNumber,
            email: formData.email,
            firstName: formData.firstName,
            lastName: formData.lastName,
            captchaToken: formData.captchaToken,
        };

        fetchData(
            withdrawOrderUrl,
            requestBody,
            (withdrawalResponse: {messageType?: string; messageId?: string} | undefined) => {
                setIsLoading(false);
                const messageType: string = withdrawalResponse?.messageType ?? '';
                const messageId: string = withdrawalResponse?.messageId ?? '';
                if (messageType.trim().toLowerCase() === BACKEND_ERROR_MESSAGE_TYPE.toLowerCase()) {
                    resetCaptcha();
                    onError(formData.orderNumber, messageId);
                } else {
                    onSuccess(formData, messageId);
                }
            },
            (error?: FetchError) => {
                setIsLoading(false);
                resetCaptcha();
                onError(formData.orderNumber, error?.body?.messageId ?? '');
            },
            {method: HttpMethods.POST},
        );
    };

    return (
        <FormProvider {...formMethods}>
            <form className="flex flex-col gap-4 mt-3" onSubmit={formMethods.handleSubmit(handleSubmit)} noValidate>
                <ControlledInputField
                    name="firstName"
                    type="text"
                    label={texts['cus__firstname']}
                    autoComplete="given-name"
                    validation={firstNameValidation(firstNameValidationMessages)}
                    required={false}
                />

                <ControlledInputField
                    name="lastName"
                    type="text"
                    label={texts['cus__surname']}
                    autoComplete="family-name"
                    validation={lastNameValidation(lastNameValidationMessages)}
                    required={false}
                />

                <ControlledInputField
                    type="email"
                    name="email"
                    required
                    autoComplete="username"
                    validation={emailValidation(emailValidationMessages)}
                    label={texts.cus__email}
                />

                <ControlledInputField
                    name="orderNumber"
                    type="text"
                    label={texts['cus__orderNumber']}
                    required
                    validation={orderNumberValidation(orderNumberValidationTexts)}
                />

                {isCaptchaEnabled && <FriendlyCaptcha {...friendlyCaptchaConfig} resetSwitch={captchaResetSwitch} />}

                <div className="grid justify-items-center gap-6 mt-3">
                    <Button
                        contentWidth
                        loading={isLoading}
                        type="submit"
                        size="large"
                        disabled={!isSubmitButtonEnabled()}
                        onClick={() => {
                            WithdrawalTracking.trackClickWithdrawContract();
                        }}
                    >
                        {texts['cus__withdrawal_withdraw_order']}
                    </Button>

                    {!isApp && (
                        <Button
                            contentWidth
                            variant="secondary"
                            size="large"
                            onClick={() => {
                                WithdrawalTracking.trackClickAbortWithdraw();
                                // Only return to the previous page when the user came from a trusted
                                // bonprix host (decided server-side via the Referer). Otherwise fall back
                                // to the home page so the form cannot bounce a user to a fake shop.
                                if (canNavigateBack) {
                                    history.back();
                                } else {
                                    window.location.assign(Url.toAbsolute(homeUrl));
                                }
                            }}
                        >
                            {texts['cus__cancel']}
                        </Button>
                    )}
                </div>
            </form>
        </FormProvider>
    );
};

export default WithdrawalForm;
